import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { extractErrorMessage } from '../lib/error.js';

export interface DeviceLink {
  id: string;
  primaryDeviceId: string;
  linkedDeviceId: string;
  createdAt: string;
  updatedAt: string;
}

const fetchDeviceLinks = async (deviceId: string): Promise<DeviceLink[]> => {
  try {
    const { data } = await apiClient.get<DeviceLink[]>(`/devices/${deviceId}/links`);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to fetch device links'));
  }
};

const addDeviceLink = async ({
  deviceId,
  linkedDeviceId,
}: {
  deviceId: string;
  linkedDeviceId: string;
}): Promise<DeviceLink> => {
  try {
    const { data } = await apiClient.post<DeviceLink>(`/devices/${deviceId}/links`, {
      linkedDeviceId,
    });
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to add device link'));
  }
};

const removeDeviceLink = async ({
  deviceId,
  linkId,
}: {
  deviceId: string;
  linkId: string;
}): Promise<void> => {
  try {
    await apiClient.delete(`/devices/${deviceId}/links/${linkId}`);
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to remove device link'));
  }
};

export const useDeviceLinks = (deviceId: string) => {
  return useQuery<DeviceLink[]>({
    queryKey: ['device-links', deviceId],
    queryFn: () => fetchDeviceLinks(deviceId),
    staleTime: 30_000,
  });
};

export const useAddDeviceLink = (deviceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkedDeviceId: string) => addDeviceLink({ deviceId, linkedDeviceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-links', deviceId] });
    },
  });
};

export const useRemoveDeviceLink = (deviceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => removeDeviceLink({ deviceId, linkId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-links', deviceId] });
    },
  });
};
